import {type Debouncer, debounce} from "@plaudit/frontend-apis";
import "@plaudit/query-loop.api";

import {
	autoUpdateSelector,
	fixSquareBracketsInURL,
	type FormishElement,
	formUpdatingClass,
	isAutoSubmittableFormishElement,
	isResettableFormishElement,
	updatingClass
} from "../../../../../../lib/shared-form-logic";
import {MirrorQueryLoopFormField} from "./MirrorQueryLoopFormField";

export class QueryLoopFormImpl implements plaudit.queryLoop.QueryLoopForm {
	private readonly formSubmitDebouncer: Debouncer<[{submitter?: Element|null, updateDelay?: number}]>;
	private readonly fetchQueue = { currentFetchId: 0, fetchQueue: Promise.resolve(new Map<string, string|string[]>()) };
	private readonly fetchNextPageQueue = { currentPage: -1, fetchQueue: Promise.resolve() };
	private readonly onChangeListeners: Array<plaudit.queryLoop.OnChangeListener> = [];

	public readonly fields = new Map<string, plaudit.queryLoop.QueryLoopFormField>();

	private fieldListMutationQueue: Promise<unknown>;

	private temporarilyLockedElements: FormishElement[] = [];

	constructor(public readonly form: HTMLFormElement) {
		this.formSubmitDebouncer = debounce(
			({submitter}) => this.submitQueryLoopForm({submitter: submitter instanceof Element ? submitter : undefined}),
			({submitter, updateDelay}) => {
				if (updateDelay !== undefined) {
					return updateDelay;
				}
				if (submitter) {
					const autoDelayStr = submitter?.closest<HTMLElement>('[data-auto-submit-delay]')?.dataset['autoSubmitDelay'];
					if (autoDelayStr !== undefined) {
						return parseInt(autoDelayStr);
					}
				}
				return 50;
			}
		);
		window.addEventListener('popstate', () => {
			const params = new URLSearchParams(document.location.search);
			if (this.isOffDefaultParameters(params)) {
				if (this.loadQueryLoopFormFieldElementsFromQueryParams(params)) {
					this.formSubmitDebouncer.clear(() => this.submitQueryLoopForm({poppingState: true}));
				}
			} else {
				this.actualResetFormQueryLoopForm(true);
			}
			for (const element of form.querySelectorAll<HTMLFieldSetElement>('fieldset.plauditQueryLoopField__fieldArea')) {
				element.dispatchEvent(new Event('input', {bubbles: false}));
			}
		});

		form.addEventListener('submit', e => {
			e.preventDefault();
			for (const dialog of form.querySelectorAll('dialog')) {
				dialog.close();
			}
			this.formSubmitDebouncer({submitter: e.submitter, updateDelay: 50});
		});
		form.addEventListener('reset', e => {
			e.preventDefault();
			for (const dialog of form.querySelectorAll('dialog')) {
				dialog.close();
			}
			this.resetQueryLoopForm(e instanceof CustomEvent && (typeof e.detail === 'string') ? e.detail : undefined);
		});
		form.addEventListener('clear', e => {
			e.preventDefault();
			for (const dialog of form.querySelectorAll('dialog')) {
				dialog.close();
			}
			this.clearQueryLoopForm(e instanceof CustomEvent && (typeof e.detail === 'string') ? e.detail : undefined);
		});

		form.addEventListener('click', e => {
			if (e.target instanceof HTMLAnchorElement && e.target.hash && e.target.closest('.plauditQueryLoop__filtersToggle')) {
				e.preventDefault();
				e.stopPropagation();
				document.getElementById(e.target.hash.substring(1))?.focus();
			}
		});

		form.addEventListener('input', e => {
			if (isAutoSubmittableFormishElement(e.target)) {
				this.handleFormSubmit(this.getField(e.target)?.field ?? e.target, undefined, true);
			}
		});
		form.addEventListener('change', e => {
			if (isAutoSubmittableFormishElement(e.target)) {
				this.handleFormSubmit(this.getField(e.target)?.field ?? e.target, undefined, true);
			}
		});

		if (!form.querySelector(".wp-block-plaudit-query-loop-results-count")) {
			const resultsCountBlock = document.createElement("div");
			resultsCountBlock.className = "wp-block-plaudit-query-loop-results-count plauditQueryLoop__resultsCount sr-only screen-reader-text";
			resultsCountBlock.setAttribute("data-one-result", "%s result");
			resultsCountBlock.setAttribute("data-some-results", "%s results");
			resultsCountBlock.setAttribute("data-no-results", "no results");
			form.appendChild(resultsCountBlock);
		}

		const promisedFields = [];
		for (const field of form.querySelectorAll<HTMLFieldSetElement>('fieldset.plauditQueryLoopField__fieldArea')) {
			promisedFields.push(plaudit.queryLoop.fieldTypes.get(field).then(c => c(field)));
		}

		const dialogCloseHandler = (e: Event) => {
			if (e.target instanceof HTMLDialogElement && getComputedStyle(e.target).position !== 'static') {
				this.submitQueryLoopForm();
			}
		}

		this.fieldListMutationQueue = Promise.all(promisedFields).then(fields => {
			for (const field of fields) {
				this.addField(field);
			}
			this.updateDefaultMarkerClasses(new URLSearchParams(window.location.search));

			for (const dialog of form.querySelectorAll('dialog')) {
				dialog.addEventListener("close", dialogCloseHandler);
			}
			new MutationObserver(mutations => {
				const promisedFields: Promise<plaudit.queryLoop.QueryLoopFormField>[] = [];
				for (const mutation of mutations) {
					for (const addedNode of mutation.addedNodes) {
						if (addedNode instanceof HTMLFieldSetElement && addedNode.classList.contains('plauditQueryLoopField__fieldArea')) {
							promisedFields.push(plaudit.queryLoop.fieldTypes.get(addedNode).then(c => c(addedNode)));
						} else if (addedNode instanceof HTMLDialogElement) {
							addedNode.addEventListener("close", dialogCloseHandler);
						} else if (addedNode instanceof HTMLElement) {
							for (const field of addedNode.querySelectorAll<HTMLFieldSetElement>('fieldset.plauditQueryLoopField__fieldArea')) {
								promisedFields.push(plaudit.queryLoop.fieldTypes.get(field).then(c => c(field)));
							}
							for (const dialog of addedNode.querySelectorAll('dialog')) {
								dialog.addEventListener("close", dialogCloseHandler);
							}
						}
					}
				}

				if (promisedFields.length) {
					this.fieldListMutationQueue = this.fieldListMutationQueue.then(() => {
						Promise.all(promisedFields).then(fields => {
							for (const field of fields) {
								this.addField(field);
							}
							this.updateDefaultMarkerClasses(new URLSearchParams(window.location.search));
						});
					});
				}
			}).observe(form, {subtree: true, childList: true});
			form.dispatchEvent(new CustomEvent('queryLoopFormInitialized', {bubbles: true, cancelable: true, detail: this}));
		});
	}

	private handleFormSubmit(field: HTMLElement, updateDelay?: number, checkBusy = false): void {
		if (checkBusy && field.classList.contains("busy")) {
			return;
		}
		if (field.classList.contains("plauditQueryLoopField__fieldArea--includedInBaseQuery") && field.querySelector(".plauditQueryLoopField__inputList")) {
			const elementsNeedingLocking = this.form
				.querySelectorAll<FormishElement>(".plauditQueryLoop__group--autoUpdate :where(input,select,textarea):not(:disabled)");
			for (const element of elementsNeedingLocking) {
				element.disabled = true;
			}
			this.temporarilyLockedElements.push(...elementsNeedingLocking);
			this.formSubmitDebouncer.clear(() => this.submitQueryLoopForm({submitter: field}));
		} else {
			this.formSubmitDebouncer({submitter: field, updateDelay});
		}
	}

	private getNonPQLFields() {
		const res = [];
		for (const field of this.form.querySelectorAll<FormishElement>('input,select,textarea')) {
			if (field.name && !field.closest('fieldset')?.classList.contains("plauditQueryLoopField__fieldArea")
				&& (!(field instanceof HTMLInputElement) || (field.type !== 'hidden' && field.type !== 'submit' && field.type !== 'reset'))
			) {
				res.push(field);
			}
		}
		return res;
	}
	private getNonPQLFieldValues() {
		const res = new Map<string, { frontendOnly: boolean, value: string|string[] }>();
		for (const field of this.getNonPQLFields()) {
			if (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox') && !field.checked) {
				continue;
			}
			const existing = res.get(field.name);
			if (field instanceof HTMLSelectElement && field.multiple) {
				if (existing === undefined) {
					res.set(field.name, {frontendOnly: field.getAttribute("data-frontend-only") === 'true', value: field.value});
				} else {
					if (!existing.frontendOnly && field.getAttribute("data-frontend-only") === 'true') {
						existing.frontendOnly = true;
					}
					if (!Array.isArray(existing.value)) {
						existing.value = [existing.value];
					}
					for (const option of field.selectedOptions) {
						existing.value.push(option.value);
					}
				}
			} else {
				if (existing === undefined) {
					res.set(field.name, {frontendOnly: field.getAttribute("data-frontend-only") === 'true', value: field.value});
				} else {
					if (!existing.frontendOnly && field.getAttribute("data-frontend-only") === 'true') {
						existing.frontendOnly = true;
					}
					if (Array.isArray(existing.value)) {
						existing.value.push(field.value);
					} else {
						existing.value = [existing.value, field.value];
					}
				}
			}
		}
		return res;
	}

	private addField(field: plaudit.queryLoop.QueryLoopFormField) {
		const existing = this.fields.get(field.name);
		if (!existing) {
			this.fields.set(field.name, field);
		} else if (!existing.isInDOM()) {
			existing.detach(); // This is to allow for cleanup
			this.fields.set(field.name, field);
		} else if (existing instanceof MirrorQueryLoopFormField) {
			existing.addField(field);
		} else {
			this.fields.set(field.name, new MirrorQueryLoopFormField([existing, field]));
		}

		this.fetchQueue.fetchQueue = this.fetchQueue.fetchQueue.then(priorState => {
			const state = new Map(priorState);
			state.set(field.name, field.getValue());
			return state;
		});
	}

	public getField(filter?: plaudit.queryLoop.GetFieldFilter|HTMLElement): plaudit.queryLoop.QueryLoopFormField|undefined {
		if (filter instanceof HTMLElement) {
			const name = filter.getAttribute("data-name") ?? filter.closest(".plauditQueryLoopField__fieldArea")?.getAttribute("data-name");
			return name ? this.fields.get(name) : undefined;
		} else if (filter !== undefined) {
			if (filter.name !== undefined) {
				return this.fields.get(filter.name);
			} else if (filter.field !== undefined) {
				const name = filter.field.closest('fieldset')?.getAttribute('data-name');
				return name ? this.fields.get(name) : undefined;
			} else if (filter.type !== undefined) {
				for (const field of this.fields.values()) {
					if (field.type === filter.type) {
						return field;
					}
				}
			}
		}
		return this.fields.size ? this.fields.values().next().value : undefined;
	}
	public getFields(filter?: plaudit.queryLoop.GetFieldFilter): plaudit.queryLoop.QueryLoopFormField[] {
		if (filter === undefined) {
			return [...this.fields.values()];
		} else if (filter.name !== undefined || filter.field !== undefined) {
			const field = this.getField(filter);
			return field !== undefined ? [field] : [];
		} else if (filter.type !== undefined) {
			const res = [];
			for (const field of this.fields.values()) {
				if (field.type === filter.type) {
					res.push(field);
				}
			}
			return res;
		}
		return [];
	}

	public getFieldValue(field: string): string|string[] {
		return this.fields.get(field)?.getValue() ?? '';
	}
	public setFieldValue(field: string, value: string|string[], updateDelay?: number) {
		const fieldInstance = this.fields.get(field);
		if (fieldInstance && fieldInstance.setValue(value)) {
			this.handleFormSubmit(fieldInstance.field, updateDelay);
		}
	}
	public removeValueFromField(field: string, value: string, updateDelay?: number) {
		const fieldInstance = this.fields.get(field);
		if (fieldInstance && fieldInstance.remove(value)) {
			this.handleFormSubmit(fieldInstance.field, updateDelay);
		}
	}

	public addOnChangeListener(listener: plaudit.queryLoop.OnChangeListener) {
		if (!this.onChangeListeners.includes(listener)) {
			this.onChangeListeners.push(listener);
		}
	}
	public removeOnChangeListener(listener: plaudit.queryLoop.OnChangeListener) {
		if (!this.onChangeListeners.includes(listener)) {
			this.onChangeListeners.splice(this.onChangeListeners.indexOf(listener), 1);
			this.onChangeListeners.push(listener);
		}
	}

	private isOffDefaultParameters(params: URLSearchParams) {
		if (params.has(`query-${this.form.dataset['queryId']}`)) {
			return true;
		} else {
			for (const field of this.fields.values()) {
				if (!field.isSetToDefaultValue()) {
					return true;
				}
			}
			for (const field of this.form.elements) {
				if (isResettableFormishElement(field) && params.has(field.name)) {
					return true;
				}
			}
		}
		return false;
	}
	private hasVisibleFieldValues(params: URLSearchParams) {
		for (const field of this.fields.values()) {
			if (field.hasVisibleValue()) {
				return true;
			}
		}
		for (const field of this.form.elements) {
			if (isResettableFormishElement(field) && params.has(field.name)) {
				return true;
			}
		}
		return false;
	}

	private loadQueryLoopFormFieldElementsFromQueryParams(params = new URLSearchParams(window.location.search)) {
		let changed = false;
		for (const field of this.fields.values()) {
			if (field.isTrackedInQuery() && field.loadFromQuery(params)) {
				changed = true;
			}
		}
		return changed;
	}

	public submitQueryLoopForm({forFullReset = false, poppingState = false, submitter = undefined}: plaudit.queryLoop.SubmitQueryLoopFormArgs = {}) {
		if (typeof window.fetch !== 'function') {
			window.location.assign(this.buildCleanURL(forFullReset, poppingState).filteredURL);
			return;
		}

		if (!submitter?.closest(".plauditQueryLoop__group--autoUpdate")) {
			for (const dialog of this.form.querySelectorAll('dialog')) {
				if (dialog.open) {
					return;
				}
			}
		}

		const myFetchId = ++this.fetchQueue.currentFetchId;
		this.fetchQueue.fetchQueue = this.fetchQueue.fetchQueue.then(priorState => {
			if (!forFullReset && this.fetchQueue.currentFetchId !== myFetchId) {
				return priorState;
			}

			const nonPQLFieldValues = this.getNonPQLFieldValues();

			if ((this.fields.size + nonPQLFieldValues.size) === priorState.size) {
				let needsFetch = false;
				let needsQuery = false;
				for (const field of this.fields.values()) {
					if (!priorState.has(field.name) || !field.isValueEqual(priorState.get(field.name)!)) {
						needsQuery = true;
						if (field.field.getAttribute("data-frontend-only") !== 'true') {
							needsFetch = true;
							break;
						}
					}
				}
				nonPQLFieldValuesTest: for (const [fieldName, {frontendOnly, value: fieldValue}] of nonPQLFieldValues.entries()) {
					const priorValue = priorState.get(fieldName);
					if (priorValue === undefined) {
						needsQuery = true;
						if (!frontendOnly) {
							needsFetch = true;
							break;
						}
					} else if (Array.isArray(priorValue)) {
						if (!Array.isArray(fieldValue) || priorValue.length !== fieldValue.length) {
							needsQuery = true;
							if (!frontendOnly) {
								needsFetch = true;
								break;
							}
						}
						for (let i = 0; i < priorValue.length; i++) {
							if (priorValue[i] !== fieldValue[i]) {
								needsQuery = true;
								if (!frontendOnly) {
									needsFetch = true;
									break nonPQLFieldValuesTest;
								}
								break;
							}
						}
					} else if (fieldValue !== priorValue) {
						// We don't need to do any special checks for fieldValue being an array because they are included in strict-equality checks
						needsQuery = true;
						if (!frontendOnly) {
							needsFetch = true;
							break;
						}
					}
				}
				if (!needsQuery) {
					return priorState;
				}
				if (!poppingState && !needsFetch) {
					const {url, filteredURL} = this.buildCleanURL(forFullReset, poppingState, nonPQLFieldValues);
					history.pushState({}, "", filteredURL);
					this.handleFormValueStateChangeEvent(url.searchParams);
					if (this.temporarilyLockedElements.length) {
						for (const element of this.temporarilyLockedElements) {
							element.disabled = false;
						}
						this.temporarilyLockedElements = [];
					}
					return this.computeCurrentState(nonPQLFieldValues);
				}
			}

			document.body.classList.toggle(formUpdatingClass, true);
			this.form.classList.toggle(formUpdatingClass, true);
			for (const ele of this.form.querySelectorAll(autoUpdateSelector)) {
				ele.classList.toggle(updatingClass, true);
			}
			const currentState = this.computeCurrentState(nonPQLFieldValues);

			const {url, filteredURL} = this.buildCleanURL(forFullReset, poppingState, nonPQLFieldValues);
			return fetch(url, this.form)
				.then(resp => resp.text())
				.then(text => new DOMParser().parseFromString(text, "text/html"))
				.then(doc => {
					if (!forFullReset && this.fetchQueue.currentFetchId !== myFetchId) {
						return priorState;
					}
					const newForm = doc.getElementById(this.form.id);
					if (newForm) {
						let i = 0;
						const orderedReplaceableElements = this.form.querySelectorAll(autoUpdateSelector);
						for (const replacement of newForm.querySelectorAll(autoUpdateSelector)) {
							orderedReplaceableElements[i++].replaceChildren(...replacement.children);
						}
					}

					for (const debugInfoDiv of doc.querySelectorAll('.plauditDebugInformation--queryLoop')) {
						document.getElementById(debugInfoDiv.id)?.replaceWith(debugInfoDiv);
					}

					const scriptsAndStylesSelector
						= ":where(link[rel='stylesheet'],style):where([id$='-css'],[id$='-rtl-css'],[id$='-inline-css'])"
						+ ",script:where([id$='-js'],[id$='-js-translations'],[id$='-js-extra'],[id$='-js-before'],[id$='-js-after'])";
					const targetDoc = this.form.ownerDocument;
					for (const element of doc.head.querySelectorAll<HTMLElement>(scriptsAndStylesSelector)) {
						const existingElement = targetDoc.getElementById(element.id);
						if (!existingElement) {
							targetDoc.head.appendChild(element);
						} else if (existingElement.innerText !== element.innerText) {
							existingElement.replaceWith(element);
						}
					}
					for (const element of doc.body.querySelectorAll<HTMLElement>(scriptsAndStylesSelector)) {
						const existingElement = targetDoc.getElementById(element.id);
						if (!existingElement) {
							targetDoc.body.appendChild(element);
						} else if (existingElement.innerText !== element.innerText) {
							existingElement.replaceWith(element);
						}
					}

					if (!poppingState) {
						history.pushState({}, "", filteredURL);
					}
					this.handleFormValueStateChangeEvent(url.searchParams);
					return currentState;
				})
				.catch(err => {
					console.log(err);
					return priorState;
				})
				.finally(() => {
					document.body.classList.toggle(formUpdatingClass, false);
					this.form.classList.toggle(formUpdatingClass, false);
					for (const ele of this.form.querySelectorAll(autoUpdateSelector)) {
						ele.classList.toggle(updatingClass, false);
					}
					if (this.temporarilyLockedElements.length) {
						for (const element of this.temporarilyLockedElements) {
							element.disabled = false;
						}
						this.temporarilyLockedElements = [];
					}
					this.form.dispatchEvent(new CustomEvent('queryLoopFormUpdateComplete', {bubbles: true, cancelable: true, detail: this}));
				});
		});
	}

	private computeCurrentState(nonPQLFieldValues: ReturnType<QueryLoopFormImpl["getNonPQLFieldValues"]>) {
		const currentState = new Map<string, string|string[]>();
		for (const field of this.fields.values()) {
			currentState.set(field.name, field.getValue());
		}
		for (const [fieldName, fieldValue] of nonPQLFieldValues.entries()) {
			currentState.set(fieldName, fieldValue.value);
		}
		return currentState;
	}

	public async fetchNextPage(): ReturnType<typeof fetch> {
		this.form.classList.toggle('loading', true);
		if (this.fetchNextPageQueue.currentPage === -1) {
			const currentQueryPageNumber = new URLSearchParams(document.location.search).get(`query-${this.form.dataset['queryId']}-page`);
			this.fetchNextPageQueue.currentPage = currentQueryPageNumber ? Math.max(parseInt(currentQueryPageNumber), 1) : 1;
		}
		const apiURL = new URL("/wp-json/plaudit/query-loop/v1/render", window.location.href);
		for (const cls of document.body.classList) {
			if (cls.startsWith('postid-')) {
				apiURL.searchParams.set('post_id', cls.substring(7));
				break;
			}
		}
		apiURL.searchParams.set('query_id', this.form.dataset['queryId']!);
		const targetPage = ++this.fetchNextPageQueue.currentPage;
		const url = this.buildCleanURL(false, targetPage, this.getNonPQLFieldValues(), apiURL.toString()).filteredURL;

		const response = await fetch(url);
		try {
			const doc = new DOMParser().parseFromString(await response.json(), "text/html");
			const appendableElements: Element[] = [];
			for (const ele of doc.querySelectorAll(`form[data-query-id="${this.form.dataset['queryId']}"] .wp-block-post-template`)) {
				if (ele.closest('form')?.id === this.form.id) {
					appendableElements.push(...ele.children);
				}
			}
			for (const ele of this.form.querySelectorAll(".wp-block-post-template")) {
				if (ele.closest('form')?.id === this.form.id) {
					ele.append(...appendableElements);
				}
			}
		} finally {
			if (targetPage === this.fetchNextPageQueue.currentPage) {
				this.form.classList.toggle('loading', false);
			}
		}
		return response;
	}

	public resetQueryLoopForm(field?: string) {
		const params = new URLSearchParams(document.location.search);
		if (this.isOffDefaultParameters(params)) {
			if (typeof field === 'string') {
				const fieldInstance = this.getField({name: field});
				if (fieldInstance?.reset()) {
					this.formSubmitDebouncer({submitter: fieldInstance.field, updateDelay: 50});
				}
			} else {
				this.actualResetFormQueryLoopForm(false);
			}
		} else if (params.has(`query-${this.form.dataset['queryId']}-page`)) {
			this.formSubmitDebouncer.clear(() => this.submitQueryLoopForm({forFullReset: true}));
		}
	}

	public clearQueryLoopForm(field?: string) {
		if (typeof field === 'string') {
			const fieldInstance = this.getField({name: field});
			if (fieldInstance?.setValue("")) {
				this.formSubmitDebouncer({submitter: fieldInstance.field, updateDelay: 50});
			}
		} else {
			for (const fieldInstance of this.getFields()) {
				fieldInstance.setValue("");
			}
			this.formSubmitDebouncer.clear(() => this.submitQueryLoopForm({forFullReset: true, poppingState: false}));
		}
	}
	private actualResetFormQueryLoopForm(poppingState: boolean) {
		for (const field of this.fields.values()) {
			field.reset();
		}
		this.formSubmitDebouncer.clear(() => this.submitQueryLoopForm({forFullReset: true, poppingState}));
	}

	private loadQueryLoopFormFieldsIntoQuery(params: URLSearchParams) {
		let hasVisibleValue = false;
		for (const field of this.fields.values()) {
			if (field.isTrackedInQuery() && field.loadIntoQuery(params)) {
				hasVisibleValue = true;
			}
		}
		return !hasVisibleValue;
	}

	public buildCleanURL(
		fullReset: boolean, poppingState: number|boolean,
		nonPQLFieldValues = this.getNonPQLFieldValues(),
		href = window.location.href
	) {
		const url = new URL(href);
		const nonDefaultStateMarkerParam = `query-${this.form.dataset['queryId']}`;

		if (fullReset) {
			let canWipeNonDefaultStateMarkerQueryParameter = true;
			for (const field of this.form.elements) {
				url.searchParams.delete((field as FormishElement).name);
				if (this.getField({field})?.isSetToDefaultValue() === false) {
					// We can't wipe the non-default state marker if the field is in a non-default state when blank.
					canWipeNonDefaultStateMarkerQueryParameter = false;
				}
			}
			for (const fieldName of nonPQLFieldValues.keys()) {
				url.searchParams.delete(fieldName);
			}
			if (canWipeNonDefaultStateMarkerQueryParameter) {
				url.searchParams.delete(nonDefaultStateMarkerParam);
			} else {
				url.searchParams.set(nonDefaultStateMarkerParam, "");
			}
		} else {
			if (this.loadQueryLoopFormFieldsIntoQuery(url.searchParams) && !(poppingState && url.searchParams.has(`query-${this.form.dataset['queryId']}-page`))) {
				url.searchParams.set(nonDefaultStateMarkerParam, "");
			} else {
				url.searchParams.delete(nonDefaultStateMarkerParam);
			}

			for (const [fieldName, {value: fieldValue}] of nonPQLFieldValues.entries()) {
				if (Array.isArray(fieldValue)) {
					url.searchParams.delete(fieldName);
					for (const value of fieldValue) {
						url.searchParams.append(fieldName, value);
					}
				} else {
					url.searchParams.set(fieldName, fieldValue);
				}
			}
		}

		if (!poppingState) {
			url.searchParams.delete(`query-${this.form.dataset['queryId']}-page`);
		} else if (typeof poppingState === 'number') {
			url.searchParams.set(`query-${this.form.dataset['queryId']}-page`, poppingState.toString());
		}
		url.searchParams.delete("cst"); // This ensures that we never include WordPress' pagination hack parameter
		url.searchParams.sort(); // Ensures stability of parameters, which should increase cache hits

		const urlString = url.toString();
		const fragmentIndex = urlString.indexOf('#');
		let page, fragment;
		if (fragmentIndex !== -1) {
			page = urlString.substring(0, fragmentIndex);
			fragment = urlString.substring(fragmentIndex);
		} else {
			page = urlString;
			fragment = "";
		}

		let filteredURL;
		const questionMarkIndex = page.indexOf('?');
		if (questionMarkIndex === -1) {
			filteredURL = urlString;
		} else {
			let query = fixSquareBracketsInURL(page).substring(questionMarkIndex)
				.replace(/=&/g, '&');
			if (query.charAt(query.length - 1) === '=') {
				query = query.substring(0, query.length - 1);
			}
			filteredURL = `${page.substring(0, questionMarkIndex)}${query}${fragment}`;
		}

		return { url, filteredURL };
	}

	private handleFormValueStateChangeEvent(params: URLSearchParams) {
		this.form.dispatchEvent(new CustomEvent('queryLoopFormValueChange', {bubbles: true, cancelable: true, detail: this}));
		for (const listener of this.onChangeListeners) {
			listener(this);
		}
		this.fetchNextPageQueue.currentPage = -1; //We need to reset the "view more" button
		this.updateDefaultMarkerClasses(params);
	}
	private updateDefaultMarkerClasses(params: URLSearchParams) {
		const valuesAreOffDefaultParameters = this.isOffDefaultParameters(params);
		this.form.classList.toggle('valuesAreDefault', !valuesAreOffDefaultParameters);
		this.form.classList.toggle('valuesAreNotDefault', valuesAreOffDefaultParameters);

		const valuesAreNotEmpty = this.hasVisibleFieldValues(params);
		this.form.classList.toggle('valuesAreEmpty', !valuesAreNotEmpty);
		this.form.classList.toggle('valuesAreNotEmpty', valuesAreNotEmpty);
	}
}
